/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/buttons/invalidateAndEditButtonBase"/>
import * as backendErrorHandler from "Core/Medius.Core.Web/Scripts/Medius/core/backendErrorHandler";
import * as ko from "knockout";
import * as rpc from "Core/Medius.Core.Web/Scripts/Medius/core/communication/json/rpc";
import * as globalization from "Core/Medius.Core.Web/Scripts/lib/globalization";
import * as documentEvents from "Core/Medius.Core.Web/Scripts/Medius/apps/document/events";
import { store } from "Core/Medius.Core.Web/Scripts/shared/store/reduxStore";
import { Module } from "Core/Medius.Core.Web/Scripts/Medius/apps/reactSpa/app";
import { InvalidatePopupTemplate } from "Core/Medius.Core.Web/Scripts/components/taskHandling/fraudPopup/InvalidatePopupTemplate";
import { InvalidatePopupInconclusiveTemplate } from "Core/Medius.Core.Web/Scripts/components/taskHandling/fraudPopup/InvalidatePopupInconclusiveTemplate";
import { RiskFactorsForInvoice } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/RiskFactor/RiskFactorComponentDtos/riskFactorsForInvoiceDto";
import { getRiskDetails, canHandleRisks, getRiskStatus } from "Core/Medius.Core.Web/Scripts/components/taskHandling/fraudPopup/service";
import { handleError } from "Core/Medius.Core.Web/Scripts/lib/errorHandling/errorHandler";
import { RejectionManagementWizardModal } from "../modals/rejectionManagementWizard/rejectionManagementWizardModal";
import { RejectedEntityType, RejectionManagementWizardType } from "../modals/rejectionManagementWizard/interfaces";
import { isRejectionManagementEnabled } from "Core/Medius.Core.Web/Scripts/Medius/core/featureToggle";
import { InvalidateAndEditConfirmationModal } from "../modals/invalidateAndEditConfirmationModal/invalidateAndEditConfirmationModal";
import { getTypeName } from "Core/Medius.Core.Web/Scripts/Medius/core/type";
import { IRejectionWizardCloseEvent } from "../modals/rejectionManagementWizard/rejectionManagementWizard";
import * as rest from "Core/Medius.Core.Web/Scripts/Medius/core/communication/json/rest";

export interface IInvalidateAndEditHandler {
    handleInvalidateAction(wasEmailSent?: boolean): void;
    getTask(): any;
    getUserTaskDto(): any;
}

export abstract class InvalidateAndEditButtonBase {
    protected invalidateAndEditHandler: IInvalidateAndEditHandler;

    private template: string;
    private isDisabled: ko.Observable<boolean>;
    private isInvaldiateAndEditHidden: ko.Observable<boolean>;
    protected isInvalidateModalVisible = ko.observable(false);
    private riskDetails: ko.Observable<RiskFactorsForInvoice> = ko.observable();
    private riskStatus = ko.observable();
    private showInvalidatePopup = ko.observable(false);
    private showIsInconclusiveInvalidatePopup = ko.observable(false);
    private showRejectionManagementWizardModal = ko.observable(false);
    private onRejectionWizardClosed?: (closeEventDetails: IRejectionWizardCloseEvent) => void;
    protected isRejectionManagementEnabledInTheStep: ko.Observable<boolean>;
    protected rejectionManagementWizardType: RejectionManagementWizardType;
    protected possibleReclassifyTypesList = ko.observable([]);
    protected currentDocumentType = ko.observable<string>();
    protected isFraudAndRiskProtectionEnabled: ko.Observable<boolean>;
    protected reasonRequiredUponRejection = ko.observable(false);

    constructor(handler: IInvalidateAndEditHandler, 
                isDisabled: ko.Observable<boolean>, 
                isInvaldiateAndEditHidden: ko.Observable<boolean> = ko.observable(false),
                isRejectionManagementEnabledInTheStep: ko.Observable<boolean> = ko.observable(false),
                companyId: number = -1) {
        this.invalidateAndEditHandler = handler;
        this.isDisabled = isDisabled;
        this.isInvaldiateAndEditHidden = isInvaldiateAndEditHidden;
        this.isRejectionManagementEnabledInTheStep = isRejectionManagementEnabledInTheStep;
        this.template = "Core:components/taskHandling/shared/buttons/invalidate.html";

        if(companyId > 0) {
            rest.get(`remedium-api/apa/rejectionManagement/companyConfiguration/${companyId}`)
            .then((companyConfiguration) => {
                this.reasonRequiredUponRejection(companyConfiguration.isReasonRequiredUponRejection ?? false);
            });
        }
    }

    public InvalidatePopup = ko.pureComputed(() => {
        return ({
            functionComponent: InvalidatePopupTemplate,
            props: {
                    showPopup: this.showInvalidatePopup(),
                    onConfirm: this.invalidate,
                    onCancel: () => this.showInvalidatePopup(false),
                    riskDetails: this.riskDetails(),
                    taskId: this.invalidateAndEditHandler.getTask().Id()
                }
            });
        });

    public InvalidatePopupInconclusive = ko.pureComputed(() => {
        return ({
            functionComponent: InvalidatePopupInconclusiveTemplate,
            props: {
                    showPopup: this.showIsInconclusiveInvalidatePopup(),
                    onConfirm: () => {
                        if(this.riskStatus()?.hasAnyRiskFactors) {
                            this.showIsInconclusiveInvalidatePopup(false);
                            if(isRejectionManagementEnabled() && this.isRejectionManagementEnabledInTheStep()) 
                            {
                                this.isFraudAndRiskProtectionEnabled = ko.observable(true);
                                this.handleInvalidateRejectionManagementWizard(); 
                            }
                            else
                            {
                                this.showInvalidatePopup(true);
                            }
                        }
                        else 
                        {
                            this.showIsInconclusiveInvalidatePopup(false);
                            this.invalidate();
                        }
                    },
                    onCancel: () => this.showIsInconclusiveInvalidatePopup(false),
                }
            });
        });

    public handleInvalidateButton = () => {
        canUserSeePopup(this.invalidateAndEditHandler.getTask().Id())
            .then(canDisplayPopup => {
                if(!canDisplayPopup) {
                    this.invalidate();
                    return;
                }
                this.handlePopups();  
            })
            .catch(error => {
                handleError(error);
            });
    };

    private handlePopups = () => {
        this.isInvalidateModalVisible(false);
        this.updateRiskStatus()
        .then(() => {
            if(this.riskStatus()?.hasAnyRiskFactors) {
                this.handlePopupsWhenInvoiceHasRiskFactors();
            }
            else {
                this.handlePopupsWhenInvoiceHasNoRiskFactors();
            }
        })
        .catch((error:any) => {
            handleError(error);
        });
    };
    
    private handlePopupsWhenInvoiceHasRiskFactors = () => {
        this.updateRiskDetails()
        .then(() => {
            if(this.riskStatus()?.isInconclusive) {
                this.showIsInconclusiveInvalidatePopup(true);
                return;
            }
    
            if(isRejectionManagementEnabled() && this.isRejectionManagementEnabledInTheStep()) 
            {
                this.isFraudAndRiskProtectionEnabled = ko.observable(true);
                this.handleInvalidateRejectionManagementWizard();
            }
            else
            {
                this.showInvalidatePopup(true);
            }
        })
        .catch((error:any) => {
            handleError(error);
        });
    };
    
    private handlePopupsWhenInvoiceHasNoRiskFactors = () => {
        if(this.riskStatus()?.isInconclusive) {
            this.showIsInconclusiveInvalidatePopup(true);
            return;
        }
        this.invalidate();
    };
     
    private updateRiskDetails = async() => {
        try {
            const riskDetails = (await getRiskDetails(this.invalidateAndEditHandler.getTask().Id())).invoiceDetails;
            this.riskDetails(riskDetails);
        }
        catch(error) {
            handleError(error);
        }
    };

    private updateRiskStatus = async () => {
        try {
            const riskStatus = await getRiskStatus(this.invalidateAndEditHandler.getTask().Id());
            this.riskStatus(riskStatus);
        }
        catch(error) {
            handleError(error);
        }
    };

    public invalidate = () => {
        if(isRejectionManagementEnabled() && this.isRejectionManagementEnabledInTheStep() && this.reasonRequiredUponRejection()) {
           this.handleInvalidateRejectionManagementWizard();
        }
        else{
            this.invalidateAndEditHandler.handleInvalidateAction();
            this.showInvalidatePopup(false);
            this.isInvalidateModalVisible(false);
        }
    };

    private handleInvalidateRejectionManagementWizard = () =>{
        this.rejectionManagementWizardType = RejectionManagementWizardType.Invalidate;
        this.openRejectionManagementWizardModal((closeEventDetails: IRejectionWizardCloseEvent) => {
            if (closeEventDetails.wasConfirmed) {
                this.invalidateAndEditHandler.handleInvalidateAction(closeEventDetails.wasEmailSent);
            }
        }, undefined, getTypeName(this.invalidateAndEditHandler.getTask().Document().$type()));
    };

    public openRejectionManagementWizardModal = (onClosed?: (closeEventDetails: IRejectionWizardCloseEvent) => void, 
        possibleReclassifyTypesList?: string[],
        currentDocumentType?: string) =>
        {
            this.onRejectionWizardClosed = onClosed; 
            this.possibleReclassifyTypesList(possibleReclassifyTypesList);
            this.showRejectionManagementWizardModal(true);          
            this.currentDocumentType(currentDocumentType);
        };

    public closeRejectionManagementWizardModal = (closeEventDetails: IRejectionWizardCloseEvent) => {
            this.showRejectionManagementWizardModal(false);
            if (this.onRejectionWizardClosed) {
                this.onRejectionWizardClosed(closeEventDetails);
                this.onRejectionWizardClosed = null;
            } 
        };

    private handleInvalidate = () => {
        this.invalidateAndEditHandler.handleInvalidateAction();
    };

    public RejectionManagementWizardModal = ko.pureComputed(() => 
         ({
                functionComponent: RejectionManagementWizardModal,
                props: {
                    wizardType: this.rejectionManagementWizardType,
                    show: this.showRejectionManagementWizardModal(),
                    documentId: this.invalidateAndEditHandler.getTask().Document().Id(),
                    hierarchyId: this.invalidateAndEditHandler.getTask().Document().Company().HierarchyId(),
                    task: this.invalidateAndEditHandler.getTask().Description(),
                    rejectedEntityType: RejectedEntityType.SupplierInvoice,
                    onClose: this.closeRejectionManagementWizardModal,
                    action: this.handleInvalidate,
                    possibleReclassifyTypesList: this.possibleReclassifyTypesList ? this.possibleReclassifyTypesList() : [],
                    currentDocumentType: this.currentDocumentType ? this.currentDocumentType() : null,
                    isFraudAndRiskProtectionEnabled: this.isFraudAndRiskProtectionEnabled ? this.isFraudAndRiskProtectionEnabled() : false,
                    riskDetails: this.riskDetails(),
                    taskId: this.invalidateAndEditHandler.getTask().Id()
                }
            }));

    public InvalidateAndEditConfirmationModal = ko.pureComputed(() =>
        ({
            functionComponent: InvalidateAndEditConfirmationModal,
            props: {
                isInvalidateModalVisible: this.isInvalidateModalVisible(),
                isInvaldiateAndEditHidden: this.isInvaldiateAndEditHidden(),
                isDisabled: this.isDisabled ? this.isDisabled() : false,
                hideInvalidateModal: this.hideInvalidateModal.bind(this),
                handleInvalidateButton: this.handleInvalidateButton.bind(this),
                invalidateAndEdit: this.invalidateAndEdit.bind(this)
            }
        }));

    abstract invalidateAndEdit(): void;

    public showInvalidateModal() {
        this.isInvalidateModalVisible(true);
    }

    public hideInvalidateModal() {
        this.isInvalidateModalVisible(false);
    }

    protected emitReclassificationEvent(task: any, targetDocumentType?: string) {
        $(window).trigger(documentEvents.documentReclassificationRequest, { task:task, targetDocumentType:targetDocumentType });
    }

    private handleReclassificationError(error: backendErrorHandler.XHR, task: any) {
        const title = globalization.getLabelTranslation("#Core/document0hasFailed", task.Document().InvoiceNumber());
        backendErrorHandler.handleAnyError(error, null, title);
    }

    protected reclassify(task: any, taskResult: any, targetDocumentType?: string) {
        rpc.request("UiDocumentManager", "EnsureCanReclassify", { taskId: task.Id(), taskResult })
            .done(() => this.emitReclassificationEvent(task, targetDocumentType))
            .fail((error: backendErrorHandler.XHR) => this.handleReclassificationError(error, task));
    }

    protected reclassifyEndTask(task: any, targetDocumentType?: string) {
        rpc.request("UiDocumentManager", "EnsureCanReclassifyEndTask", { taskId: task.Id() })
            .done(() => this.emitReclassificationEvent(task, targetDocumentType))
            .fail((error: backendErrorHandler.XHR) => this.handleReclassificationError(error, task));
    }
}

function canUserSeePopup(taskId:number) {
    const isFraudAndRiskProtectionEnabled = store.getState().accesses.enabledModules.includes(Module.FraudAndRiskProtection);
    
    if (!isFraudAndRiskProtectionEnabled) {
        return Promise.resolve(false);
    }
    return canHandleRisks(taskId)
        .then(_ => {
            return true;
        })
        .catch(error => {
            handleError(error);
        });
}